<template>
  <div class="modal-container">
    <div class="blur-backdrop" @click="close" />
    <div
      v-if="!isProratedInvoiceAvailable"
      class="modal change-or-update-num-seats"
    >
      <div class="title">
        Add or remove additional seats
      </div>
      <div class="explanatory-text change-or-update-num-seats">
        Your plan comes with {{ numStarterSeats }} starter seats included,
        and you currently have {{ currentNumAdditionalSeats }} additional seats.
        You can change the number of seats beyond the first {{ numStarterSeats }}.
      </div>
      <div>
        <div
          v-if="isSubscriptionInfoAvailable"
          class="input-row"
        >
          <span>
            {{ numStarterSeats }} starter seats
          </span>
        </div>
        <div
          v-if="isSubscriptionInfoAvailable"
          class="input-row"
        >
          <span>
            +
          </span>
          <input
            class="change-or-update-num-seats"
            :class="{'disabled': showSpinner}"
            v-model.number="numAdditionalSeats"
            type="number"
            min="0"
            step="1"
          >
          <span>
            additional seats
          </span>
        </div>
        <div
          v-if="isSubscriptionInfoAvailable"
          class="input-row last"
        >
          <span>
            = {{ numStarterSeats + numAdditionalSeats }} total seats
          </span>
        </div>
      </div>
      <SpinnerButton
        v-if="isSubscriptionInfoAvailable"
        class="button center"
        :isProcessing="showSpinner"
        :isDisabled="!canUpdateSeats"
        @click="previewProratedInvoice"
      >
        Update Seats
      </SpinnerButton>
      <SpinnerButton
        class="button danger"
        :isDisabled="showSpinner"
        @click="close"
      >
        Go Back
      </SpinnerButton>
    </div>
    <div v-if="isProratedInvoiceAvailable" class="modal change-or-update-num-seats">
      <div class="title">
        Invoice Preview for Next Billing Cycle
      </div>
      <div class="explanatory-text change-or-update-num-seats">
        You'll pay nothing now, and instead the remainder of this month will
        be prorated and included in your next invoice.
      </div>
      <div class="invoice-container">
        <div class="line-item invoice-total">
          <span class="line-item-description">
            Total amount due next billing cycle
          </span>
          <span class="line-item-price">
            US$ {{ this.proratedInvoice.amount_due / 100 }}
          </span>
        </div>
        <hr class="invoice-hr" />
        <div class="line-item">
          <span class="line-item-description header">
            Description
          </span>
          <span class="line-item-price header">
            Price
          </span>
        </div>
        <div
          v-for="[index, lineItem] in formattedInvoice.entries()"
          :key="index"
          class="line-item"
        >
          <span class="line-item-description">
            {{ lineItem.description }}
          </span>
          <span class="line-item-price">
            {{ lineItem.cost }}
          </span>
        </div>
      </div>
      <SpinnerButton
        class="button center"
        :isProcessing="isProcessingPayment"
        :isDisabled="!canUpdateSeats"
        @click="updateSubscriptionQuantity"
      >
        Confirm Payment
      </SpinnerButton>
      <SpinnerButton
        class="button danger"
        :isDisabled="isProcessingPayment"
        @click="close"
      >
        Go Back
      </SpinnerButton>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import SpinnerButton from '@/components/utilComponents/SpinnerButton';

export default Vue.extend({
  name: 'ChangeNumSeatsModal',
  components: {
    SpinnerButton
  },
  data: () => ({
    numStarterSeats: 0,
    currentNumAdditionalSeats: 0,
    numAdditionalSeats: 0,
    subscriptionInfo: {},
    isSubscriptionInfoAvailable: false,
    proratedInvoice: null,
    isProratedInvoiceAvailable: false,
    stripeCustomerId: null,
    socket: null,
    showSpinner: false,
    isProcessingPayment: false
  }),
  computed: {
    auth0UserId: function() {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    canUpdateSeats: function() {
      return Number.isInteger(this.numAdditionalSeats) &&
             this.numAdditionalSeats >= 0 &&
             this.numAdditionalSeats != this.currentNumAdditionalSeats;
    },
    formattedInvoice: function() {
      if (this.proratedInvoice) {
        return this.proratedInvoice.lines.data.map(line => {
          return {
            description: line.description,
            cost: `US$ ${ line.amount / 100 }`
          };
        });
      }

      return [];
    }
  },
  watch: {
    numAdditionalSeats: function() {
      this.validateNumAdditionalSeats();
    }
  },
  methods: {
    validateNumAdditionalSeats() {
      if (this.numAdditionalSeats) {
        if (this.numAdditionalSeats < 0) {
          this.numAdditionalSeats = null;
        } else if (!Number.isInteger(this.numAdditionalSeats)) {
          this.numAdditionalSeats = Math.floor(this.numAdditionalSeats);
        }
      }
    },
    async updateSubscriptionQuantity() {
      if (this.canUpdateSeats && !this.isProcessingPayment) {
        console.log('this.numAdditionalSeats:', this.numAdditionalSeats);
        try {
          this.isProcessingPayment = true;
          await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/updatesubscriptionquantity`, {
            auth0UserId: this.auth0UserId,
            numAdditionalSeats: this.numAdditionalSeats
          }, {
            headers: {
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${this.idToken}`
            }
          });
          await this.listenForUpdate();
        } catch (error) {
          console.log('Error:', error);
          this.isProcessingPayment = false;
        }
      }
    },
    async previewProratedInvoice() {
      if (this.canUpdateSeats) {
        console.log('this.numAdditionalSeats:', this.numAdditionalSeats);
        try {
          this.showSpinner = true;
          const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/previewinvoiceforseatchange`, {
            auth0UserId: this.auth0UserId,
            numAdditionalSeats: this.numAdditionalSeats
          }, {
            headers: {
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${this.idToken}`
            }
          });
          this.proratedInvoice = response.data.proratedInvoice;
          this.isProratedInvoiceAvailable = true;
        } catch (error) {
          console.log('Error:', error);
        }
        this.showSpinner = false;
      }
    },
    async getSubscriptionInfo() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getsubscriptioninfo`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
      this.subscriptionInfo = response.data.subscriptionInfo;
      this.numStarterSeats = this.subscriptionInfo.numStarterSeats;
      this.currentNumAdditionalSeats = this.subscriptionInfo.numAdditionalSeats;
      this.isSubscriptionInfoAvailable = true;
    },
    async listenForUpdate() {
      await this.getStripeCustomerId();
      this.connectSocket();
      await this.validateSubscriptionUpdate();
    },
    async getStripeCustomerId() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getstripecustomerid`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
      this.stripeCustomerId = response.data.stripeCustomerId;
      if (!this.stripeCustomerId) {
        // NOTE: Stripe customer ID should never be undefined here, so there's a backend problem -- send the customer home anyway
        this.$router.go();
      }
    },
    async validateSubscriptionUpdate() {
      await this.getSubscriptionInfo();
      console.log('this.subscriptionInfo:', this.subscriptionInfo);
      console.log('this.subscriptionInfo.numAdditionalSeats:', this.subscriptionInfo.numAdditionalSeats);
      console.log('this.numAdditionalSeats:', this.numAdditionalSeats);
      if (this.subscriptionInfo.numAdditionalSeats === this.numAdditionalSeats) {
        this.$router.go();
      }
    },
    connectSocket() {
      const newSocket = new WebSocket(`${ process.env.VUE_APP_BACKEND_SOCKET_URL }`);
      newSocket.onopen = () => {
        this.socket = newSocket;
        this.socket.onmessage = (event) => {
          const socketData = JSON.parse(event.data);
          const socketHandlerTable = {
            'subscriptionUpdated': () => {
              if (socketData.stripeCustomerId === this.stripeCustomerId) {
                this.validateSubscriptionUpdate();
              }
            }
          };
          if (socketData.code in socketHandlerTable) {
            socketHandlerTable[socketData.code]();
          }
        };
        this.socket.send(JSON.stringify({
          action: 'attachSubscriptionSocket',
          stripeCustomerId: this.stripeCustomerId
        }));
      };
    },
    disconnectSocket() {
      try {
        this.socket.send(JSON.stringify({
          action: 'detachSubscriptionSocket',
          stripeCustomerId: this.stripeCustomerId
        }));

        this.socket.close();
      } catch (error) {
        console.log('Error on socket disconnect:', error);
      }
    },
    close() {
      if (this.showSpinner || this.isProcessingPayment) {
        return;
      }
      this.$emit('close');
    }
  },
  created: async function() {
    try {
      await this.getSubscriptionInfo();
      this.numAdditionalSeats = this.subscriptionInfo.numAdditionalSeats;
    } catch (error) {
      console.log('Error:', error);
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/styles/modal-styles.scss';
</style>
